import React, { useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Switch,
  Button,
  Box,
} from "@mantine/core";
import { IconChevronLeft, IconExternalLink } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import CustomRichTextEditor from "../../common/RichTextEditor";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";

interface ContactContent {
  showDirections?: boolean;
  directions?: string;
  enableForm?: boolean;
  formEmail?: string;
  showMap?: boolean;
  title?: string; // REQUIRED for find_us section
  subtitle?: string; // REQUIRED for find_us section
}

interface WebsiteGeneratorContactContentProps {
  contactContent: ContactContent;
  onContentChange: (content: ContactContent) => void;
  onBack: () => void;
  missingFields?: string[];
  // Validation prop for field-level highlighting
  requiredValidation?: { [field: string]: boolean };
}

const WebsiteGeneratorContactContent: React.FC<
  WebsiteGeneratorContactContentProps
> = ({
  contactContent,
  onContentChange,
  onBack,
  missingFields = [],
  requiredValidation = {},
}) => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Handle input changes
  const handleInputChange = (field: keyof ContactContent, value: string) => {
    // Validate character limit
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: `${field} cannot exceed 100 characters`,
      }));
      return;
    }

    // Clear validation error
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Update content immediately
    onContentChange({
      ...contactContent,
      [field]: value,
    });
  };

  // Handle textarea changes
  const handleTextareaChange = (field: keyof ContactContent, value: string) => {
    // Count words
    const wordCount = value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;

    // Validate word limit
    if (wordCount > 250) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: `${field} cannot exceed 250 words`,
      }));
      return;
    }

    // Clear validation error
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Update content immediately
    onContentChange({
      ...(contactContent || {}),
      [field]: value,
    });
  };

  // Handle switch changes
  const handleSwitchChange = (
    field: keyof ContactContent,
    checked: boolean,
  ) => {
    onContentChange({
      ...(contactContent || {}),
      [field]: checked,
    });
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Contact
        </Text>
      </Group>

      {/* Show Additional Directions - HIDDEN */}
      {false && (
        <Box>
          <Group justify="space-between" align="flex-start">
            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={500} mb="xs">
                Show Additional Directions
              </Text>
              <Text size="xs" c="dimmed">
                Help clients find your location
              </Text>
            </Box>
            <Switch
              checked={contactContent.showDirections || false}
              onChange={(event) =>
                handleSwitchChange(
                  "showDirections",
                  event.currentTarget.checked,
                )
              }
              style={{ marginTop: "4px" }}
            />
          </Group>
        </Box>
      )}

      {/* Additional Directions - HIDDEN */}
      {false && contactContent.showDirections && (
        <Box>
          <CustomRichTextEditor
            label="Additional Directions"
            placeholder="Enter directions text..."
            value={contactContent.directions || ""}
            onChange={(htmlValue) =>
              handleTextareaChange("directions", htmlValue)
            }
            minHeight={150}
          />
          {validationErrors.directions && (
            <Text size="xs" c="red" mt="xs">
              {validationErrors.directions}
            </Text>
          )}
        </Box>
      )}

      {/* Enable Contact Form - HIDDEN */}
      {false && (
        <Box>
          <Group justify="space-between" align="flex-start">
            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={500} mb="xs">
                Enable Contact Form
              </Text>
              <Text size="xs" c="dimmed">
                Collect name, phone number, email, and message
              </Text>
            </Box>
            <Switch
              checked={contactContent.enableForm || false}
              onChange={(event) =>
                handleSwitchChange("enableForm", event.currentTarget.checked)
              }
              style={{ marginTop: "4px" }}
            />
          </Group>
        </Box>
      )}

      {/* Form Submissions - HIDDEN */}
      {false && contactContent.enableForm && (
        <Box>
          <Text size="sm" fw={500} mb="xs">
            Form Submissions
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            All form submissions will be sent to this email
          </Text>
          <TextInput
            placeholder="arnelle.sambile@pagoda.com"
            value={contactContent.formEmail || ""}
            onChange={(event) =>
              handleInputChange("formEmail", event.currentTarget.value)
            }
            error={validationErrors.formEmail}
            maxLength={100}
          />
        </Box>
      )}

      {/* Title (REQUIRED for find_us section) */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "title",
            requiredValidation,
            contactContent.title,
          )}
        >
          {getLabelText(
            "Title",
            "title",
            requiredValidation,
            contactContent.title,
          )}
        </Text>
        <TextInput
          placeholder="Find Us"
          value={contactContent.title || ""}
          onChange={(event) =>
            handleInputChange("title", event.currentTarget.value)
          }
          error={getFieldErrorMessage(
            "title",
            requiredValidation,
            validationErrors,
            contactContent.title,
          )}
          maxLength={100}
          styles={getInputStyles(
            "title",
            requiredValidation,
            missingFields,
            "find_us.title",
            contactContent.title,
          )}
        />
      </Box>

      {/* Subtitle (Optional - same behavior as hero's subhead) */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Subtitle
        </Text>
        <TextInput
          placeholder="Visit us at our location"
          value={contactContent.subtitle || ""}
          onChange={(event) =>
            handleInputChange("subtitle", event.currentTarget.value)
          }
          error={validationErrors.subtitle}
          maxLength={200}
        />
      </Box>

      {/* Display Map */}
      <Box>
        <Group justify="space-between" align="flex-start">
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={500} mb="xs">
              Display Map
            </Text>
            <Text size="xs" c="dimmed">
              Include map that shows business location
            </Text>
          </Box>
          <Switch
            checked={contactContent.showMap || false}
            onChange={(event) =>
              handleSwitchChange("showMap", event.currentTarget.checked)
            }
            style={{ marginTop: "4px" }}
          />
          {missingFields.some((f) => f.startsWith("google_maps")) &&
            contactContent.showMap && (
              <Text size="xs" c="red" mt="xs">
                Required fields missing for map
              </Text>
            )}
        </Group>
      </Box>

      {/* Settings Link */}
      <Group gap="xs" mt="md">
        <Text size="sm" c="dimmed">
          Visit
        </Text>
        <Button
          variant="subtle"
          color="blue"
          size="sm"
          rightSection={<IconExternalLink size={14} />}
          onClick={() => navigate("/admin/settings/studio")}
        >
          Settings &gt; Studio
        </Button>
        <Text size="sm" c="dimmed">
          to edit address and business hours.
        </Text>
      </Group>
    </Stack>
  );
};

export default WebsiteGeneratorContactContent;
